import apiClient from './apiClient';
import { db } from './firebase';
import { collection, addDoc, getDocs, updateDoc, doc, deleteDoc, onSnapshot, query, where, Timestamp, getDoc } from 'firebase/firestore';
import { createNotification } from './notificationService';
import { transactionToMySQL, deleteTransactionFromMySQL, updateToMySQL } from './transactionsMySql';
const transactionsCollection = collection(db, 'transactions');

// Helper function to validate user and license
const validateUserAndLicense = (currentUser) => {
    if (!currentUser) {
        throw new Error('Invalid user data. User information is missing.');
    }
    if (!currentUser.license) {
        throw new Error('Invalid user data. License information is missing.');
    }
    if (currentUser.license.transactionsCreated >= currentUser.license.transactionLimit) {
        throw new Error('Has alcanzado el límite de transacciones permitidas para tu licencia.');
    }
};


export const createTransaction = async (transaction, currentUser, isGroupOwner) => {
    // console.log('Creando transacción:', transaction);
    if (!currentUser.enabled) {
        throw new Error('No puedes crear transacciones porque tu cuenta aún no ha sido habilitada.');
    }

    validateUserAndLicense(currentUser);

    const now = Timestamp.now();

    const transactionItem = {
        createdAt: now,
        accountId: transaction.accountId, // Cuenta de origen
        destinationAccountId: transaction.destinationAccountId || null, // Cuenta de destino (si es transferencia)
        categoryId: transaction.categoryId || null,
        amount: parseFloat(transaction.amount),
        type: transaction.type,
        date: transaction.date,
        description: transaction.description,
        ownerUid: currentUser.uid, // Usuario que crea la transacción
        status: isGroupOwner ? 'aprobado' : 'pendiente', // Estado de la transacción
    };

    // Crear la transacción en Firestore
    const docRef = await addDoc(transactionsCollection, transactionItem);

    // Enviar la transacción a la API en PHP
    try {
        await transactionToMySQL(transaction, docRef.id, currentUser, isGroupOwner);
        // console.log('Transacción guardada en MySQL:', response);
    } catch (error) {
        console.error('Error al guardar la transacción en MySQL:', error);
        // Deshacer la creación en Firestore para mantener consistencia
        await deleteDoc(docRef);
        throw new Error('No se pudo guardar la transacción en el servidor. Por favor, intenta de nuevo.');
    }
    // Consultar la cuenta para obtener el dueño
    const accountDoc = doc(db, 'accounts', transaction.accountId);
    const accountSnapshot = await getDoc(accountDoc);

    if (!accountSnapshot.exists()) {
        throw new Error('La cuenta asociada a esta transacción no existe.');
    }

    const accountData = accountSnapshot.data();
    const accountOwnerUid = accountData.ownerUid; // El dueño de la cuenta

    // Crear notificación para el dueño de la cuenta si no es el creador
    if (!isGroupOwner && accountOwnerUid !== currentUser.uid) {
        await createNotification({
            read: false,
            recipientUid: accountOwnerUid,
            title: 'Nueva transacción pendiente',
            body: `${currentUser.displayName || 'Un colaborador'} ha creado una transacción que requiere tu aprobación.`,
            type: 'transaction',
            transactionId: docRef.id,
        });

        // Crear notificación para el creador de la transacción
        await createNotification({
            read: false,
            recipientUid: currentUser.uid,
            title: 'Transacción pendiente de aprobación',
            body: `Tu transacción ha sido enviada al dueño de la cuenta para su aprobación.`,
            type: 'transaction',
            transactionId: docRef.id,
        });
    }

    return { id: docRef.id, ...transactionItem };
};

export const createToMySQL = async (transaction, docRefId, currentUser, isGroupOwner) => {
    try {
        // Convertir la fecha al formato ISO si es necesario
        let formattedDate;
        if (transaction.date?.seconds) {
            // Si es un Timestamp de Firestore
            formattedDate = new Date(transaction.date.seconds * 1000).toISOString();
        } else if (transaction.date instanceof Date) {
            // Si ya es un objeto Date
            formattedDate = transaction.date.toISOString();
        } else if (typeof transaction.date === 'string') {
            // Si es una cadena, intentar convertirla
            const parsedDate = new Date(transaction.date);
            if (!isNaN(parsedDate.getTime())) {
                formattedDate = parsedDate.toISOString();
            } else {
                throw new Error('El campo "date" no tiene un formato válido.');
            }
        } else {
            throw new Error('El campo "date" no es válido.');
        }

        // Enviar la solicitud POST al servidor
        await apiClient.post(`/transaction.php`, {
            id: docRefId, // Usar camelCase para los nombres de las columnas
            accountId: transaction.accountId,
            destinationAccountId: transaction.destinationAccountId || null,
            categoryId: transaction.categoryId || null,
            amount: transaction.amount,
            type: transaction.type,
            transactionType: transaction.transactionType || 'default', // Nuevo campo para el tipo de transacción
            date: formattedDate, // Usar la fecha formateada
            description: transaction.description || '',
            ownerUid: currentUser.uid,
            status: isGroupOwner ? 'aprobado' : 'pendiente',
            createdAt: new Date().toISOString(), // Fecha de creación
            updatedAt: new Date().toISOString(), // Fecha de última actualización
        });
        // console.log('Transacción guardada en MySQL');
    } catch (error) {
        console.error('Error al guardar la transacción en MySQL:', error);
        throw error; // Lanzar el error para manejarlo en el lugar donde se llame esta función
    }
};

export const getTransactions = async () => {
    const snapshot = await getDocs(transactionsCollection);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};


export const updateTransaction = async (transaction) => {
    // console.log('Actualizando transacción:', transaction);

    // Referencia al documento de la transacción
    const transactionDoc = doc(db, 'transactions', transaction.id);

    // Crear una copia de los datos de la transacción
    const transactionData = {
        ...transaction,
        destinationAccountId: transaction.destinationAccountId || null, // Asegurar que exista el campo
    };

    // Validar y convertir el campo `date`
    if (transaction.date instanceof Date) {
        transactionData.date = Timestamp.fromDate(transaction.date);
    } else if (transaction.date?.seconds) {
        transactionData.date = transaction.date; // Ya es un Timestamp
    } else {
        throw new Error('El campo "date" no es válido. Debe ser un objeto Date o Timestamp.');
    }

    try {
        // Verificar si la transacción ya existe en MySQL
        const response = await apiClient.get(`/transaction.php`, {
            params: { id: transaction.id },
        });

        if (response.data.success && response.data.transaction) {
            // Actualizar la transacción en MySQL
            await updateToMySQL(transaction);

        } else {
            // console.log('La transacción no existe en MySQL. Creándola.');
            await transactionToMySQL(transaction, transaction.id, transaction.ownerUid, transaction.status);
        }

        // Actualizar la transacción en Firestore
        await updateDoc(transactionDoc, transactionData);
        // console.log('Transacción actualizada exitosamente en Firestore y MySQL.');

        // Consultar la cuenta asociada a la transacción
        const accountDoc = doc(db, 'accounts', transaction.accountId);
        const accountSnapshot = await getDoc(accountDoc);

        if (!accountSnapshot.exists()) {
            throw new Error('La cuenta asociada a esta transacción no existe.');
        }

        const accountData = accountSnapshot.data();
        const accountOwnerUid = accountData.ownerUid; // Dueño de la cuenta

        // Si el dueño de la cuenta es distinto al dueño de la transacción, enviar notificación al dueño de la transacción
        if (accountOwnerUid !== transaction.ownerUid) {
            await createNotification({
                read: false,
                recipientUid: accountOwnerUid,
                title: 'Transacción actualizada',
                body: `Tu transacción con el monto (${accountData.currency}${transaction.amount}) ha sido actualizada.`,
                type: 'transaction',
                transactionId: transaction.id,
            });
        } else {
            // Enviar notificación al dueño de la cuenta
            await createNotification({
                read: false,
                recipientUid: transaction.ownerUid,
                title: 'Transacción actualizada',
                body: `La transacción con el monto (${accountData.currency}${transaction.amount}) ha sido actualizada.`,
                type: 'transaction',
                transactionId: transaction.id,
            });
        }
    } catch (error) {
        // Si el error es 404, crear la transacción
        if (error.response && error.response.status === 404) {
            await transactionToMySQL(transaction, transaction.id, transaction.ownerUid, transaction.status);
        } else {
            console.error('Error al actualizar la transacción:', error);
            throw new Error('No se pudo actualizar la transacción. Por favor, intenta nuevamente.');
        }
    }
};

export const deleteTransaction = async (transaction) => {
    const id = transaction.id;

    try {
        // Eliminar la transacción directamente de Firestore
        const transactionDoc = doc(db, 'transactions', id);
        await deleteDoc(transactionDoc);

        // Llamar a la función para eliminar la transacción en MySQL
        await deleteTransactionFromMySQL(id);

        console.log('Transacción eliminada exitosamente.');

        // Consultar la cuenta asociada a la transacción
        const accountDoc = doc(db, 'accounts', transaction.accountId);
        const accountSnapshot = await getDoc(accountDoc);

        if (!accountSnapshot.exists()) {
            throw new Error('La cuenta asociada a esta transacción no existe.');
        }

        const accountData = accountSnapshot.data();
        const accountOwnerUid = accountData.ownerUid; // Dueño de la cuenta

        // Si el dueño de la cuenta es distinto al dueño de la transacción, enviar notificación al dueño de la transacción
        if (accountOwnerUid === transaction.ownerUid) {
            await createNotification({
                read: false,
                recipientUid: accountOwnerUid,
                title: 'Transacción eliminada',
                body: `Tu transacción con el monto (${accountData.currency}${transaction.amount}) ha sido eliminada.`,
                type: 'transaction',
                transactionId: id,
            });
        } else {
            // Enviar notificación al dueño de la cuenta
            await createNotification({
                read: false,
                recipientUid: transaction.ownerUid,
                title: 'Transacción eliminada',
                body: `La transacción con el monto (${accountData.currency}${transaction.amount}) ha sido eliminada.`,
                type: 'transaction',
                transactionId: id,
            });
        }
    } catch (error) {
        console.error(`Error al eliminar la transacción con ID ${id}:`, error);
        throw new Error('No se pudo eliminar la transacción. Por favor, intenta nuevamente.');
    }
};

export const onTransactionSnapshot = (id, callback) => {
    const transactionDoc = doc(db, 'transactions', id);
    return onSnapshot(transactionDoc, (doc) => {
        if (doc.exists()) {
            callback({ id: doc.id, ...doc.data() });
        } else {
            callback(null);
        }
    });
};

export const onTransactionsSnapshot = (accountIds, start, end, callback) => {
    if (!start || !end) {
        console.error('Las fechas de inicio y fin son obligatorias.');
        return () => { }; // Retornar una función vacía para evitar errores
    }

    const chunks = [];
    for (let i = 0; i < accountIds.length; i += 10) {
        chunks.push(accountIds.slice(i, i + 10));
    }

    const unsubscribeFunctions = [];

    chunks.forEach((chunk) => {
        if (chunk.length === 0) {
            console.warn('El chunk está vacío. Se omite el query.');
            return; // Skip this chunk
        }

        const localStart = new Date(start);
        const localEnd = new Date(end);

        // Validate that the dates are valid
        if (isNaN(localStart.getTime()) || isNaN(localEnd.getTime())) {
            // console.error('Las fechas de inicio o fin no son válidas.');
            return;
        }

        // Adjust the hours of the dates
        localStart.setHours(0, 0, 0, 0); // Set the time to the start of the day
        localEnd.setHours(23, 59, 59, 999); // Set the time to the end of the day

        // Convert the dates to Timestamps
        const startDate = Timestamp.fromDate(localStart);
        const endDate = Timestamp.fromDate(localEnd);

        let transactionsQuery = query(
            transactionsCollection,
            where('accountId', 'in', chunk),
            where('date', '>=', startDate),
            where('date', '<=', endDate)
        );

        const unsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
            const transactions = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(transaction => transaction.type !== 'initial')
                .filter(transaction => transaction.archived !== true); // Exclude archived transactions client-side
            callback(transactions);
        }, (error) => {
            console.error('Error in onSnapshot:', error);
        });

        unsubscribeFunctions.push(unsubscribe);
    });

    // Retornar una función para desuscribirse de todas las consultas
    return () => {
        unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
};

export const onCollaboratorTransactionsSnapshot = (accountIds, callback) => {
    const chunks = [];
    for (let i = 0; i < accountIds.length; i += 10) {
        chunks.push(accountIds.slice(i, i + 10));
    }

    const unsubscribeFunctions = [];

    chunks.forEach((chunk) => {
        const transactionsQuery = query(
            transactionsCollection,
            where('accountId', 'in', chunk)
        );

        const unsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
            const transactions = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(transaction => transaction.archived !== true); // Exclude archived transactions
            callback(transactions);
        });

        unsubscribeFunctions.push(unsubscribe);
    });

    // Retornar una función para desuscribirse de todas las consultas
    return () => {
        unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
};

export const approveTransaction = async (transaction, currentUser) => {
    const transactionId = transaction.id;
    const transactionDoc = doc(db, 'transactions', transactionId);

    // Actualizar el estado de la transacción a "aprobado"
    await updateDoc(transactionDoc, { status: 'aprobado' });

    // Consultar la transacción para obtener el dueño
    const transactionSnapshot = await getDoc(transactionDoc);
    if (!transactionSnapshot.exists()) {
        throw new Error('La transacción no existe.');
    }

    const transactionData = transactionSnapshot.data();
    const transactionOwnerUid = transactionData.ownerUid;

    // Consultar la cuenta asociada a la transacción para obtener el currency
    const accountDoc = doc(db, 'accounts', transactionData.accountId);
    const accountSnapshot = await getDoc(accountDoc);

    if (!accountSnapshot.exists()) {
        throw new Error('La cuenta asociada a esta transacción no existe.');
    }

    const accountData = accountSnapshot.data();
    const currency = accountData.currency || ''; // Obtener el símbolo de la moneda

    // Actualizar estatus en MySQL
    try {
        await updateToMySQL({
            id: transactionId,
            ...transactionData,
            status: 'aprobado'
        });
    } catch (mysqlError) {
        console.error('Error al actualizar estado en MySQL (Aprobar):', mysqlError);
        // No lanzamos error para no detener la notificación, pero logueamos
    }

    // Fallback for displayName
    const approverName = currentUser?.displayName || 'un colaborador';

    // Crear notificación para el dueño de la transacción
    await createNotification({
        read: false,
        recipientUid: transactionOwnerUid,
        title: 'Transacción aprobada',
        body: `Tu transacción de ${currency}${transactionData.amount} ha sido aprobada por ${approverName}.`,
        type: 'transaction',
        transactionId,
    });
};

export const rejectTransaction = async (transaction, currentUser) => {
    const transactionId = transaction.id;
    const transactionDoc = doc(db, 'transactions', transactionId);

    // Actualizar el estado de la transacción a "rechazado"
    await updateDoc(transactionDoc, { status: 'rechazado' });

    // Consultar la transacción para obtener el dueño
    const transactionSnapshot = await getDoc(transactionDoc);
    if (!transactionSnapshot.exists()) {
        throw new Error('La transacción no existe.');
    }

    const transactionData = transactionSnapshot.data();
    const transactionOwnerUid = transactionData.ownerUid;

    // Consultar la cuenta asociada a la transacción para obtener el currency
    const accountDoc = doc(db, 'accounts', transactionData.accountId);
    const accountSnapshot = await getDoc(accountDoc);

    if (!accountSnapshot.exists()) {
        throw new Error('La cuenta asociada a esta transacción no existe.');
    }

    const accountData = accountSnapshot.data();
    const currency = accountData.currency || ''; // Obtener el símbolo de la moneda

    // Actualizar estatus en MySQL
    try {
        await updateToMySQL({
            id: transactionId,
            ...transactionData,
            status: 'rechazado'
        });
    } catch (mysqlError) {
        console.error('Error al actualizar estado en MySQL (Rechazar):', mysqlError);
    }

    // Fallback for displayName
    const rejectorName = currentUser?.displayName || 'un colaborador';

    // Crear notificación para el dueño de la transacción
    await createNotification({
        read: false,
        recipientUid: transactionOwnerUid,
        title: 'Transacción rechazada',
        body: `Tu transacción de ${currency}${transactionData.amount} ha sido rechazada por ${rejectorName}.`,
        type: 'transaction',
        transactionId,
    });
};